/*  iceberg-ui  */
import { IconWithVariant } from '@auviknetworks/iceberg-ui';

/*  MUI  */
import {
    Alert,
    AlertTitle,
    Box,
    Grid,
    TableCell,
    Typography
} from '@mui/material';

/*  Shared  */
import { ApplicationNameLinkCell } from 'application/Shared/Components/ApplicationNameLinkCell';
import { Sources } from 'application/Shared/Components/Avatars';
import { Total } from 'application/Shared/Components/Cards';
import { DateFormat } from 'application/Shared/Components/Core';
import { RiskPicker } from 'application/Shared/Components/Pickers';
import { ManagedTable } from 'application/Shared/Components/Table';
import { DarkTooltip } from 'application/Shared/Components/Tooltips';
import { Usage } from 'application/Shared/Components/Usage';
import { FontSizes } from 'application/Shared/Constants';
import { Helpers } from 'application/Shared/Helpers';
import { Client } from 'application/Shared/Requests';

/*  Yarn  */
import i18n from 'i18n';

/*  Objects  */
import { Application, ApplicationBusinessOwner } from 'objects/Application';
import { User, UserEmail, UserFullName, UserTitle } from 'objects/User';

/*  React  */
import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { withRouter } from 'router/withRouter';

const headers = [
    {
        field: 'riubApplication',
        renderHeader: i18n.t('Common.application'),
        width: '20%'
    },
    {
        field: 'riubUsage',
        renderHeader: i18n.t('Common.last_used'),
        width: '10%'
    },
    {
        field: 'source',
        renderHeader: i18n.t('Common.source'),
        width: '20%'
    },
    {
        field: 'usage',
        renderHeader: i18n.t('Common.recent_usage'),
        width: '10%'
    },
    {
        field: 'riubUsernames',
        renderHeader: i18n.t('Common.usernames'),
        width: '20%'
    },
    {
        field: 'riubPercentage',
        renderHeader: i18n.t('Common.business_owner'),
        width: '20%'
    }
];

/*  State  */
const state = () => ({
    loaded: false,
    tenant: {}
});

const renderUserInfo = (user) => {
    const { id } = user;

    return (
        <User key={`user_${id}`} data={user}>
            <Box display="flex" alignItems="center">
                <Box ml="8px">
                    <Typography variant="h3" style={{ marginBottom: '0px' }}>
                        <UserFullName noTruncate link />
                    </Typography>
                    <UserTitle />
                    <br />
                    <UserEmail />
                </Box>
            </Box>
        </User>
    );
};

// Admin Offboard Report and Workflow Report Overview Section
class Overview extends Component {
    constructor(props) {
        super(props);
        this.state = state();

        const { clientId } = props.match.params;
        /* requests */
        this.Client = new Client(clientId);
    }

    componentDidMount() {
        this.load();
    }

    componentWillUnmount() {
        this.unload();
    }

    load = async () => {
        const tenant = await this.Client.get();
        this.setState({ loaded: true, tenant });
    };

    unload = () => {
        this.Client.cancel();
    };

    renderCards = (p) => {
        const { t } = this.props;
        const { totals } = p;

        return (
            <Grid
                container
                spacing={2}
                display="flex"
                justifyContent="space-between"
            >
                <Grid item xs={2}>
                    <Total title={t('Common.total_apps')} total={totals.apps} />
                </Grid>
                <Grid item xs={2}>
                    <Total
                        title={t('Offboard.sso_apps_to_remove')}
                        total={totals.ssoApps}
                    />
                </Grid>
                <Grid item xs={2}>
                    <Total
                        title={t('Offboard.partner_apps_to_remove')}
                        total={totals.partnerApps}
                    />
                </Grid>
                <Grid item xs={2}>
                    <Total
                        title={t('Offboard.client_apps_to_remove')}
                        total={totals.clientApps}
                    />
                </Grid>
                <Grid item xs={2}>
                    <Total
                        title={t('Offboard.apps_with_shared_accts')}
                        total={totals.sharedApps}
                    />
                </Grid>
            </Grid>
        );
    };

    renderUsernames = (row) => {
        const { t } = this.props;
        const { id, usernames, sharedUsernames } = row;

        if (!usernames || usernames.length === 0) {
            return null;
        }

        const render = [];
        usernames.forEach((username) => {
            const shared = sharedUsernames.includes(username);

            render.push(
                <Box
                    alignItems="center"
                    display="flex"
                    key={`${id}_username_${username}`}
                >
                    {shared && (
                        <DarkTooltip arrow title={t('Common.shared_account')}>
                            <Box key="sharedAccount">
                                <IconWithVariant
                                    variant="primary"
                                    src="Users"
                                    size={FontSizes[2]}
                                    wrapperProps={{ marginRight: '4px' }}
                                />
                            </Box>
                        </DarkTooltip>
                    )}
                    <Box style={{ overflowWrap: 'anywhere' }}>{username}</Box>
                </Box>
            );
        });

        return render;
    };

    renderRow = (row) => {
        const { tenant } = this.state;
        const {
            application,
            lastUsage,
            tenantLink,
            sources,
            usage,
            usageChart
        } = row;
        if (!application) {
            return null;
        }

        const {
            businessOwnerPlatformUserId,
            managed3rdParty,
            managedByPartner,
            noBusinessOwnerRequired
        } = tenantLink;
        const { risk } = tenantLink;

        application.businessOwnerPlatformUserId = businessOwnerPlatformUserId;

        return (
            <>
                <TableCell>
                    <Box>
                        <Application data={application} nameHasLink>
                            <ApplicationNameLinkCell nameNoTruncate />
                        </Application>
                        <Box mt="8px">
                            {risk && <RiskPicker visualOnly value={risk} />}
                        </Box>
                    </Box>
                </TableCell>
                <TableCell align="left">
                    {lastUsage === 0 ? (
                        <>
                            <Alert
                                severity="error"
                                icon={false}
                                className="no-print"
                                style={{
                                    paddingBottom: 0,
                                    paddingLeft: '8px',
                                    paddingRight: '8px',
                                    paddingTop: 0
                                }}
                            >
                                <AlertTitle style={{ margin: 0, padding: 0 }}>
                                    N/A
                                </AlertTitle>
                            </Alert>
                            <span className="print-only">N/A</span>
                        </>
                    ) : (
                        <DateFormat date={lastUsage} noTimeString />
                    )}
                </TableCell>
                <TableCell>
                    <Sources sources={sources} noBadge />
                </TableCell>
                <TableCell>
                    <Usage usageChart={usageChart} usage={usage} />
                </TableCell>
                <TableCell align="left">{this.renderUsernames(row)}</TableCell>
                <TableCell align="left">
                    <Application data={application}>
                        <ApplicationBusinessOwner
                            businessOwnerPlatformUserId={
                                businessOwnerPlatformUserId
                            }
                            noBusinessOwnerRequired={noBusinessOwnerRequired}
                            managedByPartner={managedByPartner}
                            managed3rdParty={managed3rdParty}
                            useTenantName
                            tenant={tenant}
                        />
                    </Application>
                </TableCell>
            </>
        );
    };

    renderTable = (p) => {
        const apps = Helpers.sort(p.apps, 'lastUsage');

        return (
            <ManagedTable
                headers={headers}
                noEmptyRows
                renderBody={this.renderRow}
                renderBodyKey={null}
                renderFooter={false}
                rows={apps}
                rowsPerPage={-1}
                searchEnabled={false}
                sortDirection="ASC"
                sortFieldType="number"
                sortKey="riskOrder"
            />
        );
    };

    renderSSO = (data) => {
        const { t } = this.props;
        const { ssoApps, totals } = data;
        const { googleApps, microsoftApps } = totals;
        const total = totals.ssoApps;

        return (
            <Box width="100%">
                <Typography variant="h1">
                    {t('Offboard.apps_using_sso')}
                </Typography>
                <Grid
                    container
                    spacing={2}
                    display="flex"
                    justifyContent="space-between"
                    sx={{ marginY: '8px' }}
                >
                    <Grid item xs={4}>
                        <Total
                            title={t('Offboard.apps_using_sso.total')}
                            total={total}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <Total
                            title={t('Common.google_sso')}
                            total={googleApps}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <Total
                            title={t('Common.microsoft_sso')}
                            total={microsoftApps}
                        />
                    </Grid>
                </Grid>
                <Box pt="16px">{this.renderTable({ apps: ssoApps, data })}</Box>
            </Box>
        );
    };

    renderPartnerApps = (data) => {
        const { t } = this.props;
        const { partnerApps } = data;

        return (
            <>
                <Box>
                    <Typography variant="h1">
                        {t('Offboard.other_partner_apps')}
                    </Typography>
                    <Typography style={{ fontWeight: 600 }}>
                        {t('Common.username_detected')}
                    </Typography>
                    {this.renderTable({
                        apps: partnerApps.withUsername,
                        data
                    })}
                </Box>
                <Box mt="16px">
                    <Typography style={{ fontWeight: 600 }}>
                        {t('Offboard.desktop_apps_and_no_username')}
                    </Typography>
                    {this.renderTable({
                        apps: partnerApps.noUsername,
                        data
                    })}
                </Box>
            </>
        );
    };

    renderClientApps = (data) => {
        const { t } = this.props;
        const { clientApps } = data;

        return (
            <>
                <Box>
                    <Typography variant="h1">
                        {t('Offboard.client_apps')}
                    </Typography>
                    <Typography style={{ fontWeight: 600 }}>
                        {t('Common.username_detected')}
                    </Typography>
                    {this.renderTable({
                        apps: clientApps.withUsername,
                        data
                    })}
                </Box>
                <Box mt="16px">
                    <Typography style={{ fontWeight: 600 }}>
                        {t('Offboard.desktop_apps_and_no_username')}
                    </Typography>
                    {this.renderTable({
                        apps: clientApps.noUsername,
                        data
                    })}
                </Box>
            </>
        );
    };

    renderThirdParty = (data) => {
        const { t } = this.props;
        const { thirdParty } = data;

        return (
            <>
                <Box>
                    <Typography variant="h1">
                        {t('Offboard.third_party')}
                    </Typography>
                    <Typography style={{ fontWeight: 600 }}>
                        {t('Common.username_detected')}
                    </Typography>
                    {this.renderTable({
                        apps: thirdParty.withUsername,
                        data
                    })}
                </Box>
                <Box mt="16px">
                    <Typography style={{ fontWeight: 600 }}>
                        {t('Offboard.desktop_apps_and_no_username')}
                    </Typography>
                    {this.renderTable({
                        apps: thirdParty.noUsername,
                        data
                    })}
                </Box>
            </>
        );
    };

    renderReport = (data) => {
        const { loaded } = this.state;
        const { user, totals } = data;
        const { id } = user;

        return (
            <Box key={`user_${id}`} data-trackid="renderReportOverview">
                <Box>
                    {renderUserInfo(user)}
                    <Box mt="32px">{this.renderCards({ totals })}</Box>
                    <Box mt="32px">{loaded && this.renderSSO(data)}</Box>
                </Box>
                <Box
                    mt="32px"
                    className="break-before"
                    data-trackid="reportsOffboardRenderPartnerAppsGridItem"
                >
                    {loaded && this.renderPartnerApps(data)}
                </Box>
                <Box
                    mt="32px"
                    className="break-before"
                    data-trackid="reportsOffboardRenderClientAppsGridItem"
                >
                    {loaded && this.renderClientApps(data)}
                </Box>
                <Box
                    mt="32px"
                    className="break-before"
                    data-trackid="reportsOffboardRenderThirdPartyAppsGridItem"
                >
                    {loaded && this.renderThirdParty(data)}
                </Box>
            </Box>
        );
    };

    render() {
        const { reportData } = this.props;

        if (!reportData) {
            return null;
        }

        const { length } = reportData;
        if (length === 0) {
            return null;
        }

        return (
            <Box data-trackid="mainRenderReportsOverview">
                {reportData.map((r) => this.renderReport(r))}
            </Box>
        );
    }
}

export default withRouter(withTranslation()(Overview));
